import Icon, { IconType } from "@icon-park/react/es/all";
import {
  ColorType,
  FontFamilyType,
  TextTypeAndSize,
} from "@ifgengineering/component-library";
import { ThemedText } from "@components/ThemedText";
import React, { FC } from "react";
import { useTheme } from "styled-components";
import type { DefaultTheme } from "styled-components";
import { Container, IconWrapper } from "./styled";
import SimpleTooltip from "@components/SimpleTooltip";
import { Entity } from "@ifgengineering/client-invest-sdk";

const INDIVIDUAL_INFO =
  "You are investing via your personal account. You can also choose to make an investment via one of your entities.";
const ENTITY_INFO = "You are investing via your entity account.";
const IF_ISA_INFO = "You are investing via your IF-ISA account.";

export type AccountType = "Personal" | "Entity" | "IF-ISA";
export interface EntityBadgeProps {
  entity?: Entity | null;
  hideInfo?: boolean;
  isIfIsa?: boolean;
  useAsActionButton?: boolean;
  onClick?: () => void;
  iconConfig?: {
    size?: number;
    showLabel?: boolean;
    showAccountTypeAsLabel?: boolean;
    labelSize?: TextTypeAndSize;
    hideBackground?: boolean;
    customPadding?: string;
    customBorderRadius?: string;
    showBorder?: boolean;
    borderColor?: ColorType;
    isActive?: boolean;
  };
  displayNameConfig?: {
    size?: TextTypeAndSize;
    color?: ColorType;
    fontFamily?: FontFamilyType;
    hide?: boolean;
  };
}

export interface IconData {
  popUpCopy: string;
  icon: IconType | string;
  /** Resolved off the theme, so the badge follows light and dark. */
  color: string;
  displayName: string;
  label: AccountType;
  bgColor: string;
}

/**
 * The account families the design system names, picked so the light values
 * match the palette these badges have always used.
 */
const accountPalette = (theme: DefaultTheme) => {
  const { entityTypes, premium, badgeTypes } = theme.colors.domainSpecific;
  return {
    ifisa: { color: entityTypes.ifisa, bgColor: entityTypes.ifisaBg },
    entity: { color: premium.text, bgColor: premium.background },
    personal: {
      color: badgeTypes.info.text,
      bgColor: badgeTypes.info.background,
    },
  };
};

export const EntityBadge: FC<EntityBadgeProps> = ({
  entity,
  hideInfo,
  isIfIsa,
  useAsActionButton,
  displayNameConfig,
  iconConfig,
  onClick,
}) => {
  const theme = useTheme();
  const palette = accountPalette(theme);
  let iconData: IconData = {
    popUpCopy: "",
    icon: "",
    displayName: "",
    label: "Personal",
    ...palette.personal,
  };

  if (isIfIsa) {
    iconData = {
      icon: "Funds",
      label: "IF-ISA",
      popUpCopy: IF_ISA_INFO,
      displayName: "IF-ISA",
      ...palette.ifisa,
    };
  } else if (entity?.name) {
    iconData = {
      icon: "Handbag",
      label: "Entity",
      popUpCopy: ENTITY_INFO,
      displayName: entity?.name,
      ...palette.entity,
    };
  } else {
    iconData = {
      icon: "Me",
      label: "Personal",
      popUpCopy: INDIVIDUAL_INFO,
      displayName: "Personal",
      ...palette.personal,
    };
  }

  const iconSize = `${iconConfig?.size || 14}px`;
  const showIconLabel = !!iconConfig?.showLabel;
  const iconLabel = iconConfig?.showAccountTypeAsLabel
    ? iconData.label
    : iconData.displayName;
  const iconWrapperPadding =
    showIconLabel && iconConfig?.customPadding
      ? iconConfig?.customPadding
      : showIconLabel && !iconConfig?.customPadding
      ? `8px 12px`
      : iconSize;
  const alignItems = iconConfig?.showLabel ? "center" : "flex-start";
  const borderColor =
    iconConfig?.showBorder && iconConfig?.isActive ? iconData.color : undefined;
  const backgroundColor =
    useAsActionButton && iconConfig?.isActive
      ? iconData.bgColor
      : useAsActionButton
      ? theme.colors.ui.background.raised
      : iconData.bgColor;

  return (
    <Container
      alignItems={alignItems}
      useAsActionButton={useAsActionButton}
      onClick={onClick}
      showLabel={showIconLabel}
      hideBackground={iconConfig?.hideBackground}
    >
      <IconWrapper
        bgColor={backgroundColor}
        padding={iconWrapperPadding}
        borderRadius={iconConfig?.customBorderRadius}
        showLabel={showIconLabel}
        hideBackground={iconConfig?.hideBackground}
        showBorder={iconConfig?.showBorder}
        borderColor={borderColor}
      >
        <Icon type={iconData.icon} fill={iconData.color} size={iconSize} />
        {iconConfig?.showLabel && (
          <ThemedText
            type={iconConfig?.labelSize || "S14"}
            color="SLATE700"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {iconLabel}
          </ThemedText>
        )}
      </IconWrapper>
      {!displayNameConfig?.hide && (
        <ThemedText
          type={displayNameConfig?.size || "S14"}
          color={displayNameConfig?.color || "SLATE700"}
          fontFamily={displayNameConfig?.fontFamily}
        >
          {iconData.displayName}
        </ThemedText>
      )}
      {!hideInfo && <SimpleTooltip text={iconData.popUpCopy} />}
    </Container>
  );
};

export default EntityBadge;
